/* Pocket Factory · ship dates (EXP-5): when an order is dropped off at the post office and when it arrives, worked out
   the same way everywhere it is said, so the dates never disagree.
   Two services (Bryton, Oct 31 9:32 and 9:33; BUILDLIST-2, Bryton Nov 3):
   - Standard (free): dropped off 3–5 business days after the order. Untracked, except an order of the theme's 'Free
     shipping with tracking' count (25 cards) or more, which ships with tracking: copy.tracked, and the count in the
     closest data-pf-ship-cards (the page's pack on <html>, the cart's cards on the drawer).
   - Expedited (+$10, checkout's 'Express' today): with tracking. Dropped off the next business day.
   - In transit 2–5 business days, both. Estimated arrival: the earliest drop-off + 2 to the latest drop-off + 5
     business days; Alaska, Hawaii, Puerto Rico and the territories get 3 more on the late end.
   - The day's cutoff is 6 PM in New York: an order after it, or on a weekend or a USPS holiday, counts from the next
     business day. Expedited ordered Tue 5:59 PM is dropped off Wed; Tue 6 PM, Thu. A weekend or holiday order counts its
     business days from that day itself, so Sat or Sun -> Expedited Mon (Bryton 3:15 PM Oct 31).
   - Business days are Monday to Friday, less USPS holidays: the 11 federal holidays (5 U.S.C. 6103), one on a Sunday
     kept on the Monday, one on a Saturday kept on the Saturday (post offices open on the Friday: USPS, Jul 3, 2026).
     They are worked out from the rules for any year, so there is no list to extend; a new federal holiday is added to
     HOLIDAYS. tests/exp-5-ship-dates.test.cjs pins 2026 and 2027 against USPS's own list.
   Pure date math, no request (EXP-5b's Worker endpoint was dropped for it). Node: module.exports (node --test). The
   page: window.PocketShipDates, and it fills the ship lines (data-pf-ship-eta) from the words in
   snippets/pf-exp-ship-dates.liquid, again every 30 seconds while the page is shown (the cutoff countdown). */
(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  // Once per page: the banner and the bar both load this file (BUILDLIST-2), so the second load keeps the first's timer.
  if (root && root.document && !root.PocketShipDates) {
    root.PocketShipDates = api;
    api.start(root.document);
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';
  const ZONE = 'America/New_York';
  // 6 PM in New York, in seconds after midnight.
  const CUTOFF = 18 * 3600;
  const SERVICES = { standard: { dropOff: [3, 5], tracking: false }, expedited: { dropOff: [1, 1], tracking: true } };
  /** BUILDLIST-2 (Bryton, Nov 3): free shipping says "with tracking" for an order of `from` cards or more (theme setting pf_tracked_from; 0 or unset is off). */
  const tracks = (cards, from) => Number(from) > 0 && Number(cards) >= Number(from);
  const TRANSIT = [2, 5];
  // Alaska, Hawaii, Puerto Rico, Guam, the U.S. Virgin Islands, American Samoa and the Northern Mariana Islands.
  const REMOTE = ['AK', 'HI', 'PR', 'GU', 'VI', 'AS', 'MP'], REMOTE_EXTRA = 3;
  // The federal holidays: a fixed date {m, d}, or the nth weekday of a month {m, wd, n} (wd 1 Monday; n -1 the last).
  const HOLIDAYS = [
    { m: 1, d: 1 }, { m: 1, wd: 1, n: 3 }, { m: 2, wd: 1, n: 3 }, { m: 5, wd: 1, n: -1 }, { m: 6, d: 19 }, { m: 7, d: 4 },
    { m: 9, wd: 1, n: 1 }, { m: 10, wd: 1, n: 2 }, { m: 11, d: 11 }, { m: 11, wd: 4, n: 4 }, { m: 12, d: 25 }
  ];

  // A day is 'YYYY-MM-DD', a date in New York; the arithmetic is on its UTC midnight.
  const DAY = 86400000;
  const pad = n => String(n).padStart(2, '0');
  const timeOf = day => Date.UTC(+day.slice(0, 4), +day.slice(5, 7) - 1, +day.slice(8, 10));
  const dayOf = time => new Date(time).toISOString().slice(0, 10);
  const shift = (day, n) => dayOf(timeOf(day) + n * DAY);
  const weekday = day => new Date(timeOf(day)).getUTCDay();
  function nthWeekday(year, month, wd, n) {
    if (n > 0) {
      const first = Date.UTC(year, month - 1, 1);
      return dayOf(first + ((wd - new Date(first).getUTCDay() + 7) % 7 + (n - 1) * 7) * DAY);
    }
    const last = Date.UTC(year, month, 0);
    return dayOf(last - ((new Date(last).getUTCDay() - wd + 7) % 7) * DAY);
  }
  const years = new Map();
  function keptOn(year) {
    if (!years.has(year)) {
      years.set(year, HOLIDAYS.map(h => {
        if (h.d === undefined) return nthWeekday(year, h.m, h.wd, h.n);
        const day = year + '-' + pad(h.m) + '-' + pad(h.d);
        return weekday(day) === 0 ? shift(day, 1) : day;
      }).sort());
    }
    return years.get(year);
  }
  /** The USPS holidays of a year, as the days they are kept on, in order. */
  const holidays = year => keptOn(year).slice();
  const isHoliday = day => keptOn(+day.slice(0, 4)).includes(day);
  function isBusinessDay(day) {
    const w = weekday(day);
    return w !== 0 && w !== 6 && !isHoliday(day);
  }
  function nextBusinessDay(day) {
    let next = shift(day, 1);
    while (!isBusinessDay(next)) next = shift(next, 1);
    return next;
  }
  function addBusinessDays(day, n) {
    let next = day;
    for (let i = 0; i < n; i++) next = nextBusinessDay(next);
    return next;
  }

  // The day and the time of day in New York at an instant: from Intl's time zone data, else from the U.S. rule (daylight
  // time from 2 AM on the second Sunday of March to 2 AM on the first Sunday of November: UTC-4, else UTC-5).
  let clock = null;
  function newYork(now, { intl = true } = {}) {
    const time = +now;
    if (intl) {
      try {
        clock ||= new Intl.DateTimeFormat('en-US', { timeZone: ZONE, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
        const p = {};
        for (const part of clock.formatToParts(time)) p[part.type] = part.value;
        return { day: p.year + '-' + p.month + '-' + p.day, seconds: (+p.hour % 24) * 3600 + +p.minute * 60 + +p.second };
      } catch (_) {}
    }
    const year = new Date(time).getUTCFullYear();
    const summer = time >= timeOf(nthWeekday(year, 3, 0, 2)) + 7 * 3600000 && time < timeOf(nthWeekday(year, 11, 0, 1)) + 6 * 3600000;
    const local = new Date(time - (summer ? 4 : 5) * 3600000);
    return { day: local.toISOString().slice(0, 10), seconds: local.getUTCHours() * 3600 + local.getUTCMinutes() * 60 + local.getUTCSeconds() };
  }

  /**
   * When an order placed now is dropped off and arrives. service: "standard" or "expedited" ("express" is the same).
   * state: the ship-to state's two-letter code, when known (Alaska, Hawaii, Puerto Rico and the territories arrive up to
   * 3 business days later). Days are 'YYYY-MM-DD' in New York: today, ordered (the business day the order counts from),
   * dropOff {from, to} and arrival {from, to}. cutoff: milliseconds left until 6 PM while an order now still counts
   * today (a business day before 6 PM), else null.
   */
  function estimate(service = 'standard', { now = Date.now(), state = '' } = {}) {
    const kind = service === 'express' ? 'expedited' : service, plan = SERVICES[kind];
    if (!plan) throw new Error('Unknown shipping service: ' + service);
    const ny = newYork(now), today = ny.day, open = isBusinessDay(today) && ny.seconds < CUTOFF;
    const ordered = open ? today : nextBusinessDay(today);
    // Day 0: today when open or not a business day (a weekend order's next business day is day 1), else the next one.
    const base = open || !isBusinessDay(today) ? today : ordered;
    const dropOff = { from: addBusinessDays(base, plan.dropOff[0]), to: addBusinessDays(base, plan.dropOff[1]) };
    const late = TRANSIT[1] + (REMOTE.includes(String(state || '').trim().toUpperCase()) ? REMOTE_EXTRA : 0);
    const arrival = { from: addBusinessDays(dropOff.from, TRANSIT[0]), to: addBusinessDays(dropOff.to, late) };
    return { service: kind, today, ordered, dropOff, arrival, tracking: plan.tracking, cutoff: open ? (CUTOFF - ny.seconds) * 1000 : null };
  }

  /** A day as the page says it: "Tue, Nov 4" (the page's language, else English). */
  function format(day, locale) {
    const options = { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' };
    try { return new Intl.DateTimeFormat(locale || 'en-US', options).format(timeOf(day)); }
    catch (_) { return new Intl.DateTimeFormat('en-US', options).format(timeOf(day)); }
  }

  // ---- The page: the ship lines (data-pf-ship-eta="standard"|"expedited", data-pf-ship-form="full"|"short"|"bar") ----
  const escape = text => String(text).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const fill = (template, values) => Object.keys(values).reduce((text, key) => text.split('[' + key + ']').join(values[key]), String(template || ''));
  /**
   * The words of each ship line, as HTML, from copy (snippets/pf-exp-ship-dates.liquid: the store's locale, already
   * HTML): an estimate's arrival, and for Expedited the day it is dropped off (ships: "ships tomorrow") and, before the
   * cutoff, the time left to make that day (when: "order in 3 h 12 m to ship tomorrow", else the same as ships). Only the
   * dates this script writes are escaped. Each service has its full, short and bar forms ('' where the copy has none) and
   * its estimate; with copy.tracked (BUILDLIST-2), the forms it gives that service say "with tracking" (tracked.full,
   * .short, .bar), for an order of copy.tracked.from cards or more.
   */
  function lines(copy, { now = Date.now(), locale = 'en-US' } = {}) {
    const say = day => escape(format(day, locale)), out = {};
    for (const kind of ['standard', 'expedited']) {
      const e = estimate(kind, { now });
      const day = e.dropOff.from === shift(e.today, 1) ? copy.tomorrow : say(e.dropOff.from), ships = fill(copy.ships, { day });
      let when = ships;
      if (e.cutoff !== null) {
        const minutes = Math.ceil(e.cutoff / 60000), hours = Math.floor(minutes / 60);
        const time = hours ? fill(copy.hoursMinutes, { hours, minutes: minutes % 60 }) : fill(copy.minutes, { minutes });
        when = fill(copy.orderIn, { time, day });
      }
      const values = { arrival: fill(copy.range, { from: say(e.arrival.from), to: say(e.arrival.to) }), when, ships };
      const forms = set => ({ full: set?.full ? fill(set.full, values) : '', short: set?.short ? fill(set.short, values) : '', bar: set?.bar ? fill(set.bar, values) : '' });
      out[kind] = { ...forms(copy[kind]), estimate: e };
      if (copy.tracked?.[kind]) out[kind].tracked = forms(copy.tracked[kind]);
    }
    return out;
  }
  const painted = new WeakMap();
  function paint(doc, now = Date.now()) {
    const source = doc.querySelector('[data-pf-ship-copy]'), targets = doc.querySelectorAll('[data-pf-ship-eta]');
    if (!source || !targets.length) return;
    let copy;
    try { copy = JSON.parse(source.textContent); } catch (_) { return; }
    const words = lines(copy, { now, locale: doc.documentElement.lang || 'en-US' });
    const from = Number(copy.tracked?.from) || 0;
    for (const node of targets) {
      const set = words[node.dataset.pfShipEta];
      const form = node.dataset.pfShipForm === 'short' ? 'short' : node.dataset.pfShipForm === 'bar' ? 'bar' : 'full';
      // BUILDLIST-2: the closest count decides (the drawer's own, else the page's); no count, no tracking.
      const cards = Number(node.closest('[data-pf-ship-cards]')?.dataset.pfShipCards) || 0;
      const html = (tracks(cards, from) && set?.tracked?.[form]) || set?.[form];
      // Unchanged words are left alone; without its words (no template) a line keeps what the page printed.
      if (!html || painted.get(node) === html) continue;
      node.innerHTML = html;
      painted.set(node, html);
    }
  }
  const started = new WeakSet();
  function start(doc) {
    if (started.has(doc)) return;
    started.add(doc);
    const run = () => { if (doc.visibilityState !== 'hidden') paint(doc); };
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', run, { once: true });
    else run();
    // The countdown and the day after the cutoff; again as the page is shown, and after the theme editor redraws.
    (doc.defaultView || globalThis).setInterval(run, 30000);
    doc.addEventListener('visibilitychange', run);
    doc.addEventListener('shopify:section:load', run);
  }

  return { estimate, lines, paint, start, format, tracks, holidays, isBusinessDay, nextBusinessDay, addBusinessDays, newYork, ZONE, CUTOFF, REMOTE };
});
